/**
 * Fashion outfits' data (types only imported: looks/outfits.ts reads it while sprites.ts is still loading). Fashion outfits (lane C0-OUTFIT, user-approved 2026-10-10; research docs/research/C0_OUTFITS.md, review
 * docs/review/c0-outfits/): ten full-body outfits, each for a man and a woman, as rig uniforms — one layer per slot
 * (top, bottom, robe, shoes, belt, cape, pauldrons, headwear, ornaments) so they split into parts for free
 * customisation — plus the outline-breaking parts (flowParts.ts: flying scarf, shoulder drape, wide sleeves).
 * Worn through the look's `fashion` (the fashion skins `fashion-<id>`, looks/outfits.ts; sets.ts `appearanceOf`),
 * in place of the clothes and school uniform; the wearer keeps their own face, weapon and (unless the outfit sets
 * one) hairstyle. Garment types are costume history; colourways and combinations are our own.
 */
import type { Look } from '../looks/types';
import type { Extra } from './flowParts';
import type { Uniform } from './uniforms';

/** One body's version of an outfit: the clothes, plus the hair and weapon that go with them. */
export interface OutfitBody extends Uniform {
  hairStyle?: Look['hairStyle'];
  /** The weapon the sample is shown with in the studio (the game keeps the wearer's own). */
  weapon: Look['weapon'];
  /** Parts that break the outline (flowParts.ts): a flying scarf, a cape's shoulder drape, wide sleeves. */
  extras?: readonly Extra[];
}

export interface OutfitDesign {
  id: string;
  /** Player-facing name (Thai). */
  label: string;
  /** One line on the idea and its signature shape (Thai). */
  note: string;
  male: OutfitBody;
  female: OutfitBody;
}

// The master's palette plus each role's colours (research §4).
const WHITE = '#ece4d2';
const INK = '#1e1a22';
const GOLD = '#d8aa48';
const GOLD_S = '#96701e';

export const FASHION: readonly OutfitDesign[] = [
  {
    id: 'wanderer',
    label: 'จอมยุทธ์พเนจร',
    note: 'ชุดเดินทางคราม หมวกงอบไม้ไผ่ ผ้าพันคอแดงปลิว',
    male: {
      top: { id: 'bracer', colour: '#e4dccb', trim: '#3a4660', inner: WHITE, accent: '#a8242a' },
      // The leg wraps a muted tone (BODY dressed-r3 E3: the light wraps showed as stray white dots on the shin).
      bottom: { id: 'wrapped', colour: '#2c2a34', wrap: '#6a6272' },
      robe: {
        id: 'travel',
        colour: '#46557a',
        trim: '#2a3048',
        inner: WHITE,
        sash: '#5a3a26',
        accent: '#cbbfa6',
        // BODY dressed-r2 D4: a narrower slit with a fold line, the hem lifted so the boot tops show.
        slitScale: 0.5,
        slitFill: 'fold',
        hemLift: 1.5,
      },
      shoes: { id: 'boots', colour: '#3a2a22' },
      belt: { id: 'leather', colour: '#5a3a26', buckle: GOLD },
      headwear: ['douli'],
      weapon: 'sword',
      extras: [{ id: 'scarf', colour: '#b0262c', len: 34 }],
    },
    female: {
      top: { id: 'swordmaid', colour: '#46557a', trim: '#2a3048', inner: WHITE, accent: '#a8242a' },
      bottom: { id: 'slim', colour: '#2c2a34', wrap: '#cbbfa6' },
      shoes: { id: 'ankleboots', colour: '#3a2a22' },
      belt: { id: 'bow', colour: '#5a3a26', buckle: GOLD },
      robe: {
        id: 'swordlady',
        colour: '#46557a',
        trim: '#2a3048',
        inner: WHITE,
        sash: '#5a3a26',
        accent: '#cbbfa6',
        // BODY dressed-r2 D3 / r3 E2: a narrow dark slit with one lighter line, the hem flaring less.
        slitScale: 0.6,
        slitFill: 'seam',
        hemFlare: 2,
      },
      headwear: ['douli'],
      weapon: 'sword',
      extras: [{ id: 'scarf', colour: '#b0262c', len: 34 }],
    },
  },
  {
    id: 'mistpeak',
    label: 'ศิษย์สำนักยอดหมอก',
    note: 'นักพรตชุดเทาหมอกขลิบหมึก แขนเสื้อกว้างห้อย มงกุฎรวบผม ชายผ้าคาดเอวยาว',
    male: {
      top: { id: 'cross', colour: '#d9dcdc', trim: '#2a2d38', inner: '#f0eee8', accent: '#5e8a74' },
      bottom: { id: 'wrapped', colour: '#2a2d38', wrap: '#d9dcdc' },
      robe: {
        id: 'daopao',
        colour: '#c9cfd2',
        trim: '#2a2d38',
        inner: '#f0eee8',
        sash: '#2a2d38',
        accent: '#5e8a74',
        hem: 'clouds',
      },
      shoes: { id: 'cloth', colour: '#1a1a20' },
      headwear: ['guan'],
      regalia: [
        { id: 'jade', c: '#bfe0cc', s: '#5e8a74', a: '#2a2d38' },
        { id: 'sashTails', c: '#2a2d38', s: '#16171e', a: '#5e8a74' },
      ],
      hairStyle: 'topknot',
      weapon: 'sword',
      extras: [{ id: 'sleeves', colour: '#c9cfd2', trim: '#2a2d38', len: 8 }],
    },
    female: {
      top: { id: 'cross', colour: '#d9dcdc', trim: '#2a2d38', inner: '#f0eee8', accent: '#5e8a74' },
      bottom: { id: 'skirt', colour: '#2a2d38', wrap: '#d9dcdc' },
      robe: {
        id: 'priestess',
        colour: '#c9cfd2',
        trim: '#2a2d38',
        inner: '#f0eee8',
        sash: '#2a2d38',
        accent: '#5e8a74',
        hem: 'clouds',
      },
      shoes: { id: 'slippers', colour: '#1a1a20' },
      headwear: ['pin'],
      regalia: [
        { id: 'jade', c: '#bfe0cc', s: '#5e8a74', a: '#2a2d38' },
        { id: 'sashTails', c: '#2a2d38', s: '#16171e', a: '#5e8a74' },
      ],
      hairStyle: 'bun',
      weapon: 'sword',
      extras: [{ id: 'sleeves', colour: '#c9cfd2', trim: '#2a2d38', len: 8 }],
    },
  },
  {
    id: 'bellrock',
    label: 'ศิษย์สำนักหินระฆัง',
    note: 'กางเกงพระสีหญ้าฝรั่น จีวรแดงเลือดหมูพาดบ่า ลูกประคำไม้ ไม้พลอง',
    male: {
      top: { id: 'bare', colour: '#c9782a', trim: '#6a3218', inner: '#e8d6aa', accent: GOLD },
      bottom: { id: 'monk', colour: '#c9782a', wrap: '#e8d6aa' },
      robe: {
        id: 'kasaya',
        colour: '#7a2a20',
        trim: GOLD,
        inner: '#c9782a',
        sash: '#6a3218',
        accent: '#4e1a12',
      },
      shoes: { id: 'wrapped', colour: '#4a3424' },
      regalia: [{ id: 'mala', c: '#6a3a1c', s: '#3a1c0c', a: '#a8242a' }],
      hairStyle: 'messy',
      weapon: 'staff',
    },
    female: {
      top: { id: 'quiltedf', colour: '#c9782a', trim: '#6a3218', inner: '#e8d6aa', accent: GOLD },
      bottom: { id: 'culottes', colour: '#7a2a20', wrap: '#e8d6aa' },
      shoes: { id: 'wrapped', colour: '#4a3424' },
      belt: { id: 'obi', colour: '#6a3218', buckle: GOLD },
      regalia: [{ id: 'mala', c: '#6a3a1c', s: '#3a1c0c', a: '#a8242a' }],
      hairStyle: 'bun',
      weapon: 'staff',
      extras: [{ id: 'drape', colour: '#7a2a20', edge: '#d8aa48', len: 9 }],
    },
  },
  {
    id: 'ironlantern',
    label: 'ทางโคมเหล็ก',
    note: 'ชุดยาวดำเหล็กขลิบอำพัน ผ้าคลุมดำพาดบ่า หมวกขุนนางปีก ป้ายทองที่เอว ดาบหลังเดียว',
    male: {
      top: { id: 'mandarin', colour: '#1c1c24', trim: '#d88a2a', inner: '#1c1c24', accent: GOLD },
      bottom: { id: 'trousers', colour: '#1c1c24', wrap: '#d88a2a' },
      robe: {
        id: 'feiyu',
        colour: '#2a2a34',
        trim: '#d88a2a',
        inner: '#1c1c24',
        sash: '#1c1c24',
        accent: GOLD,
        hem: 'waves',
      },
      shoes: { id: 'tallboots', colour: '#16161c' },
      belt: { id: 'jade', colour: '#1c1c24', buckle: GOLD },
      headwear: ['futou'],
      regalia: [{ id: 'token', c: GOLD, s: GOLD_S, a: '#d88a2a' }],
      weapon: 'sabre',
      extras: [{ id: 'drape', colour: '#1c1c24', edge: '#d88a2a', clasp: '#d8aa48', len: 12 }],
      cape: { id: 'long', colour: '#1c1c24', clasp: '#d88a2a' },
    },
    female: {
      top: { id: 'longstand', colour: '#2a2a34', trim: '#d88a2a', inner: '#1c1c24', accent: GOLD },
      bottom: { id: 'slim', colour: '#1c1c24', wrap: '#d88a2a' },
      shoes: { id: 'highboots', colour: '#16161c' },
      belt: { id: 'wide', colour: '#1c1c24', buckle: GOLD },
      cape: { id: 'long', colour: '#1c1c24', clasp: '#d88a2a' },
      regalia: [{ id: 'token', c: GOLD, s: GOLD_S, a: '#d88a2a' }],
      hairStyle: 'ponytail',
      weapon: 'sabre',
      extras: [{ id: 'drape', colour: '#1c1c24', edge: '#d88a2a', clasp: '#d8aa48', len: 10 }],
    },
  },
  {
    id: 'dimmoon',
    label: 'ศิษย์หุบเขาจันทร์มัว',
    note: 'ม่วงราตรีขลิบเงินจันทร์ ผ้าแพรชมพูคล้องแขน ชายผ้าพู่ห้อย',
    male: {
      top: { id: 'open', colour: '#3a2050', trim: '#c4c8d8', inner: '#e8dce6', accent: '#c8608e' },
      bottom: { id: 'wide', colour: '#1e1228', wrap: '#c4c8d8' },
      robe: {
        id: 'coat',
        colour: '#2a1838',
        trim: '#c4c8d8',
        inner: '#3a2050',
        sash: '#c8608e',
        accent: '#c8608e',
      },
      shoes: { id: 'curled', colour: '#1a1020' },
      regalia: [{ id: 'tassels', c: '#c8608e', s: '#8e3862', a: '#c4c8d8' }],
      weapon: 'sword',
      extras: [{ id: 'scarf', colour: '#c8608e', len: 30 }],
    },
    female: {
      top: { id: 'crop', colour: '#3a2050', trim: '#c4c8d8', inner: '#e8dce6', accent: '#c8608e' },
      bottom: { id: 'splitskirt', colour: '#2a1838', wrap: '#c8608e' },
      shoes: { id: 'lotus', colour: '#1a1020' },
      belt: { id: 'tassels', colour: '#c8608e', buckle: '#c4c8d8' },
      cape: { id: 'shawl', colour: '#d88cb0', clasp: '#c4c8d8' },
      headwear: ['butterfly'],
      weapon: 'sword',
      extras: [{ id: 'sleeves', colour: '#d88cb0', trim: '#c4c8d8', len: 6 }],
    },
  },
  {
    id: 'inkpine',
    label: 'ศิษย์หอหมึกสน',
    note: 'บัณฑิตชุดขาวกระดาษขลิบเขียวสน แขนเสื้อกว้างยาว ผ้าคาดหมึก หยกห้อยเอว',
    male: {
      top: { id: 'cross', colour: '#3e6a4a', trim: '#f0ebdc', inner: '#f0ebdc', accent: INK },
      bottom: { id: 'trousers', colour: '#2a3028', wrap: '#f0ebdc' },
      robe: {
        id: 'scholar',
        colour: '#f0ebdc',
        trim: '#3e6a4a',
        inner: '#3e6a4a',
        sash: INK,
        accent: '#3e6a4a',
        hem: 'bamboo',
      },
      shoes: { id: 'cloth', colour: INK },
      headwear: ['band'],
      regalia: [{ id: 'jade', c: '#8ccaa2', s: '#3e6a4a', a: INK }],
      hairStyle: 'bun',
      weapon: 'none',
      extras: [{ id: 'sleeves', colour: '#f0ebdc', trim: '#3e6a4a', len: 10 }],
    },
    female: {
      top: { id: 'ao', colour: '#f0ebdc', trim: '#3e6a4a', inner: '#f0ebdc', accent: INK },
      bottom: { id: 'pleated', colour: '#9ab8a0', wrap: '#f0ebdc' },
      robe: {
        id: 'ruqun',
        colour: '#f0ebdc',
        trim: '#3e6a4a',
        inner: '#9ab8a0',
        sash: INK,
        accent: '#3e6a4a',
        hem: 'bamboo',
      },
      shoes: { id: 'slippers', colour: INK },
      headwear: ['flower'],
      regalia: [{ id: 'jade', c: '#8ccaa2', s: '#3e6a4a', a: INK }],
      weapon: 'none',
      extras: [{ id: 'sleeves', colour: '#f0ebdc', trim: '#3e6a4a', len: 10 }],
    },
  },
  {
    id: 'cinnabar',
    label: 'ศิษย์สำนักดาบชาด',
    note: 'ชุดศึกดำ ผ้าแดงชาด เกราะไหล่ข้างเดียว ผ้าคลุมพาดบ่า ดาบหลังเดียว',
    male: {
      top: { id: 'bracer', colour: '#b8321e', trim: INK, inner: '#2a1a18', accent: '#8a6a3a' },
      bottom: { id: 'greaves', colour: '#221818', wrap: '#4a2a22' },
      robe: {
        id: 'battle',
        colour: '#221618',
        trim: '#b8321e',
        inner: '#b8321e',
        sash: '#3a1612',
        accent: '#8a6a3a',
        hem: 'flames',
      },
      shoes: { id: 'armoured', colour: '#161012' },
      pauldrons: { id: 'single', plate: '#3a2a26', edge: '#8a6a3a' },
      cape: { id: 'tattered', colour: '#b8321e', clasp: '#8a6a3a' },
      weapon: 'sabre',
      extras: [{ id: 'drape', colour: '#221618', edge: '#b8321e', len: 12 }],
    },
    female: {
      top: { id: 'sleevelessf', colour: '#b8321e', trim: INK, inner: '#2a1a18', accent: '#8a6a3a' },
      bottom: { id: 'splitskirt', colour: '#221618', wrap: '#b8321e' },
      shoes: { id: 'highboots', colour: '#161012' },
      belt: { id: 'wide', colour: '#3a1612', buckle: '#8a6a3a' },
      pauldrons: { id: 'winged', plate: '#3a2a26', edge: '#8a6a3a' },
      regalia: [{ id: 'sashTails', c: '#b8321e', s: '#7a1a10', a: INK }],
      hairStyle: 'ponytail',
      weapon: 'sabre',
      extras: [{ id: 'drape', colour: '#b8321e', edge: '#221618', len: 10 }],
      cape: { id: 'long', colour: '#b8321e', clasp: '#8a6a3a' },
    },
  },
  {
    id: 'guard',
    label: 'ราชองครักษ์',
    note: 'เกราะเกล็ดเหล็กบนชุดแดงเลือดหมู หมวกเกราะ บ่าหัวสิงห์ ผ้าคลุมยาว ทวน',
    male: {
      top: { id: 'lamellar', colour: '#8a919c', trim: GOLD, inner: '#6e1a20', accent: '#6e1a20' },
      bottom: { id: 'greaves', colour: '#3a1418', wrap: '#8a919c' },
      robe: {
        id: 'armour',
        colour: '#8a919c',
        trim: GOLD,
        inner: '#6e1a20',
        sash: '#3a1418',
        accent: GOLD,
      },
      shoes: { id: 'armoured', colour: '#2a2a30' },
      belt: { id: 'wide', colour: '#3a1418', buckle: GOLD },
      pauldrons: { id: 'lion', plate: '#8a919c', edge: GOLD },
      cape: { id: 'long', colour: '#8a1e24', clasp: GOLD },
      headwear: ['helmet'],
      weapon: 'staff',
      extras: [{ id: 'drape', colour: '#8a1e24', edge: '#d8aa48', clasp: '#d8aa48', len: 12 }],
    },
    female: {
      top: { id: 'quiltedf', colour: '#6e1a20', trim: GOLD, inner: '#8a919c', accent: GOLD },
      bottom: { id: 'slim', colour: '#3a1418', wrap: '#8a919c' },
      shoes: { id: 'highboots', colour: '#2a2a30' },
      belt: { id: 'wide', colour: '#3a1418', buckle: GOLD },
      pauldrons: { id: 'layered', plate: '#8a919c', edge: GOLD },
      cape: { id: 'long', colour: '#8a1e24', clasp: GOLD },
      headwear: ['helmet'],
      hairStyle: 'ponytail',
      weapon: 'staff',
      extras: [{ id: 'drape', colour: '#8a1e24', edge: '#d8aa48', clasp: '#d8aa48', len: 11 }],
    },
  },
  {
    id: 'nightblade',
    label: 'นักฆ่ารัตติกาล',
    note: 'ชุดรัดรูปดำม่วง ผ้าปิดหน้า ชาย: ฮู้ดและผ้าคลุมสั้น หญิง: หางม้ากับผ้าพันคอแดง',
    male: {
      top: { id: 'bracer', colour: '#221c2a', trim: '#3a3044', inner: '#2a2432', accent: '#8a1e24' },
      bottom: { id: 'wrapguard', colour: '#1c1822', wrap: '#3a3044' },
      shoes: { id: 'wrapped', colour: '#141118' },
      belt: { id: 'pouches', colour: '#3a2a26', buckle: '#8a8e98' },
      headwear: ['hood', 'mask'],
      weapon: 'sword',
      extras: [{ id: 'drape', colour: '#2e2836', edge: '#1c1822', len: 9 }],
      cape: { id: 'short', colour: '#2e2836', clasp: '#8a8e98' },
    },
    female: {
      top: { id: 'sleevelessf', colour: '#221c2a', trim: '#3a3044', inner: '#2a2432', accent: '#8a1e24' },
      bottom: { id: 'slim', colour: '#1c1822', wrap: '#3a3044' },
      shoes: { id: 'ankleboots', colour: '#141118' },
      belt: { id: 'pouches', colour: '#3a2a26', buckle: '#8a8e98' },
      headwear: ['mask'],
      hairStyle: 'ponytail',
      regalia: [{ id: 'sashTails', c: '#8a1e24', s: '#5a1016', a: '#221c2a' }],
      weapon: 'sword',
      extras: [{ id: 'scarf', colour: '#8a1e24', len: 24 }],
    },
  },
  {
    id: 'hunter',
    label: 'พรานแดนเหนือ',
    note: 'เสื้อนวมหนังสัตว์ ผ้าคลุมขนครีมพาดบ่า บู๊ตขน ผ้าโพกหัว',
    male: {
      top: { id: 'quilted', colour: '#7a5236', trim: '#e8dcc0', inner: '#c8a070', accent: '#a8482a' },
      bottom: { id: 'wrapped', colour: '#4a3626', wrap: '#c8a070' },
      robe: {
        id: 'furcloak',
        colour: '#6a4630',
        trim: '#e8dcc0',
        inner: '#7a5236',
        sash: '#a8482a',
        accent: '#a8482a',
      },
      shoes: { id: 'furboots', colour: '#4a3424' },
      belt: { id: 'pouches', colour: '#3a2a1e', buckle: '#b88a4a' },
      headwear: ['bandana'],
      weapon: 'sabre',
      extras: [{ id: 'drape', colour: '#e8dcc0', edge: '#a89878', len: 7 }],
    },
    female: {
      top: { id: 'quiltedf', colour: '#7a5236', trim: '#e8dcc0', inner: '#c8a070', accent: '#a8482a' },
      bottom: { id: 'slim', colour: '#4a3626', wrap: '#c8a070' },
      robe: {
        id: 'furcape',
        colour: '#6a4630',
        trim: '#e8dcc0',
        inner: '#7a5236',
        sash: '#a8482a',
        accent: '#a8482a',
      },
      shoes: { id: 'furboots', colour: '#4a3424' },
      belt: { id: 'leather', colour: '#3a2a1e', buckle: '#b88a4a' },
      headwear: ['feather'],
      hairStyle: 'ponytail',
      weapon: 'sabre',
      extras: [{ id: 'drape', colour: '#e8dcc0', edge: '#a89878', len: 7 }],
    },
  },
];
